import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/lib/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ANON_SESSION_KEY = 'you_anon_session_id';
const PENDING_GEN_KEY = 'you_pending_generation_id';
const PENDING_GEN_TS_KEY = 'you_pending_generation_timestamp';
const MAX_POLL_TICKS = 60;   // 5 min at 5s intervals
const MAX_POLL_ERRORS = 5;   // consecutive failures before circuit breaker trips
const STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes — discard older generations

const getOrCreateAnonSession = () => {
  let sessionId = localStorage.getItem(ANON_SESSION_KEY);
  if (!sessionId) {
    sessionId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(ANON_SESSION_KEY, sessionId);
  }
  return sessionId;
};

const GenerationContext = createContext(null);

export const useGeneration = () => {
  const ctx = useContext(GenerationContext);
  if (!ctx) throw new Error('useGeneration must be used within GenerationProvider');
  return ctx;
};

export const GenerationProvider = ({ children }) => {
  const { user, session, incrementAnonCounter, getAccessToken } = useAuth();

  // ─── Generation state ─────────────────────────────────────────
  const [generationId, setGenerationId] = useState(null);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [slides, setSlides] = useState([]);
  const [contentPreview, setContentPreview] = useState('');
  const [cliffhangerQuestion, setCliffhangerQuestion] = useState('');
  const [nextTopicFromStory, setNextTopicFromStory] = useState('');

  // Selection state (what the user chose before generating)
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedBoard, setSelectedBoard] = useState(
    () => localStorage.getItem('you_board') || 'CBSE'
  );
  const [selectedClass, setSelectedClass] = useState(
    () => localStorage.getItem('you_class') || 'class_5_6'
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    () => localStorage.getItem('you_language') || 'te'
  );
  const [selectedHero, setSelectedHero] = useState(null);

  // Quiz state
  const [quizData, setQuizData] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState([]);

  // Polling refs
  const pollRef = useRef(null);
  const pollTickRef = useRef(0);
  const pollErrorRef = useRef(0);

  // Current slide index for audio player sync
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  // Poll count exposed for UI (e.g. progress indicator)
  const [pollCount, setPollCount] = useState(0);

  // ─── Auth helpers ─────────────────────────────────────────────
  const getAuthHeaders = useCallback(() => {
    const token = getAccessToken();
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [getAccessToken]);

  // ─── Polling ──────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((genId) => {
    stopPolling();
    pollTickRef.current = 0;
    pollErrorRef.current = 0;
    setPollCount(0);

    pollRef.current = setInterval(async () => {
      pollTickRef.current += 1;
      setPollCount(pollTickRef.current);

      // Timeout: stop after MAX_POLL_TICKS (5 min)
      if (pollTickRef.current > MAX_POLL_TICKS) {
        stopPolling();
        setIsGenerating(false);
        setGenerationError('Generation is taking too long. Please try again.');
        localStorage.removeItem(PENDING_GEN_KEY);
        localStorage.removeItem(PENDING_GEN_TS_KEY);
        // Best-effort cancel on backend so zombie jobs don't accumulate
        axios.post(`${API}/generation/${genId}/cancel`).catch(() => {});
        return;
      }

      try {
        const res = await axios.get(`${API}/generation/${genId}`);
        const data = res.data;
        pollErrorRef.current = 0; // reset on success
        setGenerationStatus(data.status);

        if (data.status === 'completed') {
          stopPolling();
          setIsGenerating(false);
          localStorage.removeItem(PENDING_GEN_KEY);
          localStorage.removeItem(PENDING_GEN_TS_KEY);
          setAudioUrl(data.audio_url || null);
          setSlides(data.slides || []);
          setContentPreview(data.content_preview || '');
          setCliffhangerQuestion(data.cliffhanger || '');
          setNextTopicFromStory(data.next_topic || '');
          setCurrentSlideIndex(0);

          // Save to local story history
          try {
            const _hist = JSON.parse(localStorage.getItem('you_story_history') || '[]');
            _hist.unshift({
              id: data.id || Date.now(),
              topic: data.content_preview || '',
              language: localStorage.getItem('you_language') || 'te',
              board: localStorage.getItem('you_board') || '',
              classGroup: localStorage.getItem('you_class') || '',
              timestamp: new Date().toISOString(),
              score: null,
            });
            if (_hist.length > 50) _hist.pop();
            localStorage.setItem('you_story_history', JSON.stringify(_hist));
          } catch (err) { console.warn('Generation: history save:', err?.message || err); }
        } else if (data.status === 'failed') {
          stopPolling();
          setIsGenerating(false);
          localStorage.removeItem(PENDING_GEN_KEY);
          localStorage.removeItem(PENDING_GEN_TS_KEY);
          setGenerationError(data.error || 'Generation failed. Please try again.');
        }
      } catch (err) {
        console.error('Poll error:', err);
        pollErrorRef.current += 1;
        if (pollErrorRef.current >= MAX_POLL_ERRORS) {
          stopPolling();
          setIsGenerating(false);
          localStorage.removeItem(PENDING_GEN_KEY);
          localStorage.removeItem(PENDING_GEN_TS_KEY);
          setGenerationError('Network error. Please check your connection and try again.');
        }
      }
    }, 5000);
  }, [stopPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  // Resume polling if page was refreshed during generation
  useEffect(() => {
    const pendingId = localStorage.getItem(PENDING_GEN_KEY);
    if (!pendingId || pollRef.current || isGenerating) return;

    // Stale check: discard generations older than 10 minutes
    const storedTs = parseInt(localStorage.getItem(PENDING_GEN_TS_KEY) || '0');
    if (Date.now() - storedTs > STALE_THRESHOLD_MS) {
      localStorage.removeItem(PENDING_GEN_KEY);
      localStorage.removeItem(PENDING_GEN_TS_KEY);
      return;
    }

    // Check job status before resuming
    axios.get(`${API}/generation/${pendingId}`)
      .then(res => {
        const status = res.data?.status;
        if (status === 'failed' || status === 'error') {
          localStorage.removeItem(PENDING_GEN_KEY);
          localStorage.removeItem(PENDING_GEN_TS_KEY);
          setGenerationError(res.data.error || 'Previous generation failed. Please try again.');
          return;
        }
        if (status === 'completed') {
          localStorage.removeItem(PENDING_GEN_KEY);
          localStorage.removeItem(PENDING_GEN_TS_KEY);
          setIsGenerating(false);
          setAudioUrl(res.data.audio_url || null);
          setSlides(res.data.slides || []);
          setContentPreview(res.data.content_preview || '');
          setCliffhangerQuestion(res.data.cliffhanger || '');
          setNextTopicFromStory(res.data.next_topic || '');
          setCurrentSlideIndex(0);
          return;
        }
        // Still processing — resume polling
        setGenerationId(pendingId);
        setIsGenerating(true);
        setGenerationStatus(status || 'processing');
        startPolling(pendingId);
      })
      .catch(() => {
        // Job not found or network error — stale ID, clean up
        localStorage.removeItem(PENDING_GEN_KEY);
        localStorage.removeItem(PENDING_GEN_TS_KEY);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Core generation action ───────────────────────────────────
  const startGeneration = useCallback(async (content, { isAnonymous = false } = {}) => {
    // Reset to clean state before every generation (Netflix/Spotify pattern)
    stopPolling();
    setGenerationId(null);
    setGenerationStatus('processing');
    setGenerationError(null);
    setIsGenerating(true);
    setAudioUrl(null);
    setSlides([]);
    setContentPreview('');
    setCliffhangerQuestion('');
    setNextTopicFromStory('');
    setCurrentSlideIndex(0);
    setQuizData([]);
    setQuizAnswers([]);
    setPollCount(0);
    localStorage.removeItem(PENDING_GEN_KEY);
    localStorage.removeItem(PENDING_GEN_TS_KEY);

    // Track selection
    if (content.type === 'text') setSelectedTopic(content.text || '');

    try {
      const formData = new FormData();
      formData.append('input_type', content.type);
      const currentLanguage = localStorage.getItem('you_language') || 'te';
      formData.append('language', currentLanguage);
      formData.append('board', localStorage.getItem('you_board') || 'CBSE');
      formData.append('class_group', localStorage.getItem('you_class') || 'class_5_6');
      if (content.type === 'text') formData.append('text_content', content.text);
      else if (content.file) formData.append('file', content.file);

      const makeRequest = async (authHdrs) => {
        const headers = { 'Content-Type': 'multipart/form-data', ...authHdrs };
        if (isAnonymous) {
          headers['X-Anon-Session-Id'] = getOrCreateAnonSession();
        }
        return axios.post(`${API}/generate`, formData, { headers });
      };

      let res;
      try {
        res = await makeRequest(getAuthHeaders());
      } catch (firstErr) {
        // 401 = expired token — refresh once and retry
        if (firstErr.response?.status === 401) {
          const { supabase } = await import('@/lib/supabase');
          const { data: { session: refreshed } } = await supabase.auth.refreshSession();
          if (refreshed?.access_token) {
            const retryHeaders = { Authorization: `Bearer ${refreshed.access_token}` };
            res = await makeRequest(retryHeaders);
          } else {
            throw firstErr;
          }
        } else {
          throw firstErr;
        }
      }

      // Increment anonymous counter AFTER successful API response
      incrementAnonCounter();
      setGenerationId(res.data.id);
      localStorage.setItem(PENDING_GEN_KEY, res.data.id);
      localStorage.setItem(PENDING_GEN_TS_KEY, Date.now().toString());
      startPolling(res.data.id);
    } catch (err) {
      setIsGenerating(false);
      setGenerationStatus(null);
      if (err.response?.status === 429) {
        setGenerationError('Slow down! Your pet needs a breather. Try again in a minute.');
      } else if (err.response?.status === 401) {
        setGenerationError('Session expired. Please sign in again.');
      } else if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        if (detail === 'ANONYMOUS_LIMIT_REACHED') {
          setGenerationError('ANONYMOUS_LIMIT_REACHED');
        } else {
          setGenerationError(detail || 'Something went wrong. Please try again.');
        }
      } else if (err.response?.status === 400 && err.response?.data?.error === 'topic_blocked') {
        setGenerationError(err.response.data.message);
      } else {
        setGenerationError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incrementAnonCounter, getAuthHeaders, startPolling, stopPolling]);

  // ─── Reset generation (return to idle) ────────────────────────
  const resetGeneration = useCallback(() => {
    stopPolling();
    setGenerationId(null);
    setGenerationStatus(null);
    setGenerationError(null);
    setIsGenerating(false);
    setAudioUrl(null);
    setSlides([]);
    setContentPreview('');
    setCliffhangerQuestion('');
    setNextTopicFromStory('');
    setCurrentSlideIndex(0);
    setQuizData([]);
    setQuizAnswers([]);
    setPollCount(0);
    setSelectedTopic('');
    setSelectedSubject('');
    setSelectedHero(null);
    localStorage.removeItem(PENDING_GEN_KEY);
    localStorage.removeItem(PENDING_GEN_TS_KEY);
  }, [stopPolling]);

  // ─── Context value ────────────────────────────────────────────
  const value = {
    // State
    generationId,
    generationStatus,
    generationError,
    isGenerating,
    audioUrl,
    slides,
    contentPreview,
    cliffhangerQuestion,
    nextTopicFromStory,
    currentSlideIndex,
    pollCount,
    quizData,
    quizAnswers,
    selectedTopic,
    selectedSubject,
    selectedBoard,
    selectedClass,
    selectedLanguage,
    selectedHero,

    // Setters (for components that need direct control)
    setGenerationError,
    setCurrentSlideIndex,
    setQuizData,
    setQuizAnswers,
    setSelectedTopic,
    setSelectedSubject,
    setSelectedBoard,
    setSelectedClass,
    setSelectedLanguage,
    setSelectedHero,

    // Actions
    startGeneration,
    stopPolling,
    resetGeneration,
  };

  return (
    <GenerationContext.Provider value={value}>
      {children}
    </GenerationContext.Provider>
  );
};
